import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePublicOrder } from "../context/PublicOrderContext";
import api from "../utils/api";
import CustomerNavbar from "./CustomerNavbar";
import { Armchair, ShoppingBag, Eye, HelpCircle } from "lucide-react";
import { toast } from "react-toastify";

const TableSelection = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setOrderInfo, setOrderId } = usePublicOrder();

  // Safeguard: Redirect if table lock is active
  useEffect(() => {
    const isLocked = sessionStorage.getItem("table_lock_active") === "true";
    const storedOrderId = localStorage.getItem(`orderId_${slug}`);
    if (isLocked && storedOrderId) {
      console.log("[QR Lock] Bypassing table selection due to active table lock.");
      navigate(`/r/${slug}/menu?orderId=${storedOrderId}`);
    }
  }, [slug, navigate]);


  const [layout, setLayout] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeName, setStoreName] = useState("");
  const [storeLogo, setStoreLogo] = useState("");
  const [restaurantData, setRestaurantData] = useState(null);

  // 🔹 Fetch tables
  useEffect(() => {
    const fetchTables = async () => {
      try {
        setLoading(true);

        const [tablesRes, storeRes] = await Promise.all([
          api.get(`/api/public/restaurant/${slug}/free-tables`),
          api.get(`/api/public/restaurant/${slug}`)
        ]);

        setLayout(tablesRes.data.floors || []);
        setStoreName(storeRes.data.name);
        setStoreLogo(storeRes.data.logo);
        setRestaurantData(storeRes.data);

      } catch (err) {
        console.error("Failed to fetch tables", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTables();
  }, [slug]);

  const handleSelect = async (floor, section, unit) => {
    try {
      const res = await api.post(`/api/public/restaurant/${slug}/order`, {
        orderType: "DINE_IN",
        floorName: floor.floorName,
        sectionName: section.name,
        unitNumber: unit.unitNumber,
        roomType: section.roomType || null,
        sectionType: section.type,
      });

    setOrderInfo(res.data);
      setOrderId(res.data._id);

      navigate(`/r/${slug}/menu?orderId=${res.data._id}`);
    } catch (err) {
      console.error("Failed to create order", err);
      const message = err.response?.data?.message || "Failed to select table. Please try again.";
      toast.error(message, {
        position: "top-center",
        autoClose: 3000
      });
    }
  };

  const handleTakeaway = async () => {
    try {
      setLoading(true);
      const res = await api.post(
        `/api/public/restaurant/${slug}/order`,
        { orderType: "DIRECT" }
      );

      setOrderInfo(res.data);
      setOrderId(res.data._id);
      sessionStorage.removeItem("table_lock_active");

      navigate(`/r/${slug}/menu?orderId=${res.data._id}`);
    } catch (err) {
      console.error("Failed to create takeaway order", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (restaurantData?.orderCapabilities?.dineInEnabled === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <CustomerNavbar storeName={storeName} storeLogo={storeLogo} slug={slug} />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="text-6xl mb-4 opacity-50">🚫</div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Dine-In Disabled</h2>
          <p className="text-slate-500 max-w-md mb-8">
            This restaurant is currently not accepting Dine-In digital orders.
          </p>
          <button
            onClick={() => navigate(`/r/${slug}`)}
            className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-full font-bold shadow-md transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!layout.length) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white p-8 rounded-3xl shadow-xl border border-gray-100 text-center animate-scale-up">
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-500 mx-auto mb-5 shadow-inner">
            <Armchair className="w-8 h-8" />
          </div>

          <h2 className="text-2xl font-black text-gray-900 mb-2">Dining Tables Full</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-8">
            All of our dine-in tables and rooms are currently occupied. We are working hard to clear a spot for you! In the meantime, you can place a takeaway order or browse our menu catalog.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleTakeaway}
              className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 text-white font-extrabold text-sm rounded-xl transition shadow-md hover:scale-[1.01] flex items-center justify-center gap-2 uppercase tracking-wider"
            >
              <ShoppingBag className="w-4 h-4" />
              Order Takeaway / Parcel
            </button>

            <button
              onClick={() => navigate(`/r/${slug}/menu?mode=view`)}
              className="w-full py-3 border border-gray-250 hover:bg-slate-50 text-gray-700 font-extrabold text-sm rounded-xl transition flex items-center justify-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Browse Menu Catalog
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-center gap-1.5 text-xs text-gray-400 font-bold uppercase tracking-wider">
            <HelpCircle className="w-4 h-4 text-gray-300" />
            <span>Need Help? Ask our floor staff</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Navbar */}
      <CustomerNavbar
        storeName={storeName}
        storeLogo={storeLogo}
        slug={slug}
      />

      <div className="pt-28 px-6 pb-10">

        <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
          Select Your Table
        </h1>

        <p className="text-center text-gray-500 mb-10">
          Choose a table or room to continue your dine-in order.
        </p>

        <div className="space-y-10">

          {layout.map((floor) => (
            <div key={floor.floorName}>

              {/* Floor Header */}
              <div className="mb-4">
                <h2 className="text-xl font-bold text-teal-700">
                  {floor.floorName}
                </h2>
                <div className="h-1 w-16 bg-teal-500 rounded mt-1"></div>
              </div>

              {floor.sections.map((section) => (
                <div key={section.name} className="mb-8">

                  {/* Section Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">
                      {section.name}
                    </h3>

                    {section.type === "ROOM" && section.roomType && (
                      <span
                        className={`px-3 py-1 text-xs rounded-full font-medium ${
                          section.roomType === "AC"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-orange-100 text-orange-700"
                        }`}
                      >
                        {section.roomType === "AC"
                          ? "❄️ AC"
                          : "🌤 Non-AC"}
                      </span>
                    )}
                  </div>

                  {/* Units Grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-4">

                    {section.units.map((unit) => (
                      <div
                        key={unit.unitNumber}
                        onClick={() => handleSelect(floor, section, unit)}
                        className="h-20 bg-white rounded-xl shadow-md 
                                   flex flex-col items-center justify-center
                                   cursor-pointer transition 
                                   hover:shadow-xl hover:-translate-y-1"
                      >
                        <div className="text-2xl mb-1">
                          {section.type === "TABLE" ? "🪑" : "🛏"}
                        </div>

                        <div className="font-semibold text-gray-700 text-sm">
  {section.type === "TABLE" ? "T" : "R"}
  {unit.unitNumber}
</div>

                      </div>
                    ))}

                  </div>
                </div>
              ))}
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};

export default TableSelection;
